import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PassportCard from "@/components/PassportCard";
import PassportCompletion from "@/components/PassportCompletion";
import BadgeGrid from "@/components/BadgeGrid";
import Timeline from "@/components/Timeline";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Briefcase, GraduationCap, Wrench, Trophy, Star } from "lucide-react";

export default function Passport() {
  const { user, refresh } = useAuth();
  const [passport, setPassport] = useState(null);
  const [editProfile, setEditProfile] = useState(false);
  const [profile, setProfile] = useState({});

  const load = async () => {
    const { data } = await api.get("/profile/me");
    setPassport(data);
  };
  useEffect(() => {
    load();
  }, []);
  useEffect(() => {
    if (passport?.user) setProfile(passport.user);
  }, [passport?.user]);

  const saveProfile = async () => {
    try {
      await api.patch("/profile", {
        headline: profile.headline,
        bio: profile.bio,
        location: profile.location,
        website: profile.website,
        visibility: profile.visibility,
      });
      toast.success("Passport updated");
      setEditProfile(false);
      await load();
      await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (!passport) {
    return (
      <div className="min-h-screen bg-ec-bg">
        <Navbar />
        <div className="max-w-6xl mx-auto px-6 py-20 eyebrow">Loading your passport…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ec-bg" data-testid="passport-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-10 space-y-8">
        <PassportCard user={passport.user} score={passport.score} />

        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7">
            <PassportCompletion completion={passport.completion} />
          </div>
          <div className="lg:col-span-5 border border-ec-line bg-white p-6 rounded-sm" data-testid="passport-badges">
            <div className="eyebrow mb-3">Badges earned</div>
            <BadgeGrid badges={passport.badges || []} />
          </div>
        </div>

        <div className="border border-ec-line bg-white p-6 rounded-sm" data-testid="passport-timeline-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="eyebrow">Career timeline</div>
              <h3 className="font-display font-bold text-xl mt-1">Your journey, signed.</h3>
            </div>
          </div>
          <Timeline items={passport.timeline || []} />
        </div>

        <div className="flex justify-end">
          <Dialog open={editProfile} onOpenChange={setEditProfile}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-sm border-ec-ink" data-testid="edit-profile-trigger">
                Edit profile
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-display">Edit your passport</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <Field label="Headline">
                  <Input
                    value={profile.headline || ""}
                    onChange={(e) => setProfile({ ...profile, headline: e.target.value })}
                    data-testid="edit-headline-input"
                  />
                </Field>
                <Field label="Bio">
                  <Textarea
                    value={profile.bio || ""}
                    onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                  />
                </Field>
                <Field label="Location">
                  <Input
                    value={profile.location || ""}
                    onChange={(e) => setProfile({ ...profile, location: e.target.value })}
                  />
                </Field>
                <Field label="Website">
                  <Input
                    value={profile.website || ""}
                    onChange={(e) => setProfile({ ...profile, website: e.target.value })}
                  />
                </Field>
                <Field label="Visibility">
                  <Select
                    value={profile.visibility || "public"}
                    onValueChange={(v) => setProfile({ ...profile, visibility: v })}
                  >
                    <SelectTrigger className="rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="public">Public</SelectItem>
                      <SelectItem value="private">Private</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <DialogFooter>
                <Button onClick={saveProfile} className="rounded-sm bg-ec-ink hover:bg-zinc-800" data-testid="save-profile-button">
                  Save
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <Tabs defaultValue="employment" className="w-full" data-testid="passport-tabs">
          <TabsList className="bg-ec-mute rounded-sm flex flex-wrap h-auto">
            <TabsTrigger value="employment" className="rounded-sm gap-2"><Briefcase className="w-4 h-4" />Employment</TabsTrigger>
            <TabsTrigger value="education" className="rounded-sm gap-2"><GraduationCap className="w-4 h-4" />Education</TabsTrigger>
            <TabsTrigger value="skills" className="rounded-sm gap-2"><Wrench className="w-4 h-4" />Skills</TabsTrigger>
            <TabsTrigger value="achievements" className="rounded-sm gap-2"><Trophy className="w-4 h-4" />Achievements</TabsTrigger>
            <TabsTrigger value="recommendations" className="rounded-sm gap-2"><Star className="w-4 h-4" />Recommendations</TabsTrigger>
          </TabsList>

          <TabsContent value="employment" className="mt-6">
            <EmploymentSection items={passport.employments} reload={load} />
          </TabsContent>
          <TabsContent value="education" className="mt-6">
            <EducationSection items={passport.educations} reload={load} />
          </TabsContent>
          <TabsContent value="skills" className="mt-6">
            <SkillsSection items={passport.skills} reload={load} />
          </TabsContent>
          <TabsContent value="achievements" className="mt-6">
            <AchievementsSection items={passport.achievements} reload={load} />
          </TabsContent>
          <TabsContent value="recommendations" className="mt-6">
            <RecommendationsSection items={passport.recommendations} reload={load} />
          </TabsContent>
        </Tabs>
      </div>
      <Footer />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="eyebrow">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function SectionShell({ title, addLabel, children, dialog, testidPrefix }) {
  return (
    <Card className="rounded-sm border-ec-line" data-testid={`${testidPrefix}-section`}>
      <CardHeader className="border-b border-ec-line flex flex-row items-center justify-between space-y-0">
        <CardTitle className="font-display">{title}</CardTitle>
        {dialog}
      </CardHeader>
      <CardContent className="p-0">{children}</CardContent>
    </Card>
  );
}

function EmptyState({ label }) {
  return (
    <div className="p-10 text-center text-ec-ink-soft text-sm">
      {label}
    </div>
  );
}

// ---------------- Employment ----------------
function EmploymentSection({ items, reload }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ organization: "", title: "", start_date: "", end_date: "", description: "" });
  const submit = async () => {
    try {
      await api.post("/profile/employment", f);
      toast.success("Employment added");
      setOpen(false);
      setF({ organization: "", title: "", start_date: "", end_date: "", description: "" });
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const del = async (id) => {
    await api.delete(`/profile/employment/${id}`);
    reload();
  };
  return (
    <SectionShell
      title="Employment History"
      testidPrefix="employment"
      dialog={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-sm bg-ec-ink hover:bg-zinc-800 gap-2" data-testid="add-employment-trigger"><Plus className="w-4 h-4" /> Add</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-display">Add employment</DialogTitle>
            </DialogHeader>
            <Field label="Organization"><Input value={f.organization} onChange={(e) => setF({ ...f, organization: e.target.value })} /></Field>
            <Field label="Title"><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Start date"><Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} /></Field>
              <Field label="End date"><Input type="date" value={f.end_date || ""} onChange={(e) => setF({ ...f, end_date: e.target.value })} /></Field>
            </div>
            <Field label="Description"><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
            <DialogFooter>
              <Button onClick={submit} className="rounded-sm bg-ec-ink hover:bg-zinc-800" data-testid="save-employment-button">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      }
    >
      {items.length === 0 ? <EmptyState label="No employment records yet. Add your first to start building your score." /> : (
        <ul className="divide-y divide-ec-line">
          {items.map((it) => (
            <li key={it.id} className="p-6 flex items-center justify-between gap-4">
              <div>
                <div className="font-display font-semibold text-lg">{it.title}</div>
                <div className="text-sm text-ec-ink-soft">{it.organization} · {it.start_date} – {it.end_date || "Present"}</div>
                {it.description && <p className="text-sm mt-2">{it.description}</p>}
                {it.verified ? <Badge className="bg-ec-emerald/10 text-ec-emerald border-ec-emerald/30 mt-2">Verified</Badge> : <Badge variant="outline" className="mt-2 text-ec-ink-soft">Self-declared</Badge>}
              </div>
              <Button variant="ghost" size="icon" onClick={() => del(it.id)} data-testid={`delete-employment-${it.id}`}><Trash2 className="w-4 h-4" /></Button>
            </li>
          ))}
        </ul>
      )}
    </SectionShell>
  );
}

// ---------------- Education ----------------
function EducationSection({ items, reload }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ institution: "", degree: "", field: "", start_year: "", end_year: "", grade: "" });
  const submit = async () => {
    try {
      await api.post("/profile/education", { ...f, start_year: parseInt(f.start_year), end_year: f.end_year ? parseInt(f.end_year) : null });
      toast.success("Education added");
      setOpen(false);
      setF({ institution: "", degree: "", field: "", start_year: "", end_year: "", grade: "" });
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const del = async (id) => { await api.delete(`/profile/education/${id}`); reload(); };
  return (
    <SectionShell title="Education" testidPrefix="education" dialog={
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild><Button className="rounded-sm bg-ec-ink hover:bg-zinc-800 gap-2" data-testid="add-education-trigger"><Plus className="w-4 h-4" /> Add</Button></DialogTrigger>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Add education</DialogTitle></DialogHeader>
          <Field label="Institution"><Input value={f.institution} onChange={(e) => setF({ ...f, institution: e.target.value })} /></Field>
          <Field label="Degree"><Input value={f.degree} onChange={(e) => setF({ ...f, degree: e.target.value })} /></Field>
          <Field label="Field of study"><Input value={f.field} onChange={(e) => setF({ ...f, field: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start year"><Input type="number" value={f.start_year} onChange={(e) => setF({ ...f, start_year: e.target.value })} /></Field>
            <Field label="End year"><Input type="number" value={f.end_year} onChange={(e) => setF({ ...f, end_year: e.target.value })} /></Field>
          </div>
          <Field label="Grade / GPA"><Input value={f.grade} onChange={(e) => setF({ ...f, grade: e.target.value })} /></Field>
          <DialogFooter><Button onClick={submit} className="rounded-sm bg-ec-ink hover:bg-zinc-800" data-testid="save-education-button">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    }>
      {items.length === 0 ? <EmptyState label="No education records yet." /> : (
        <ul className="divide-y divide-ec-line">
          {items.map((it) => (
            <li key={it.id} className="p-6 flex items-center justify-between gap-4">
              <div>
                <div className="font-display font-semibold text-lg">{it.degree}</div>
                <div className="text-sm text-ec-ink-soft">{it.institution} · {it.start_year} – {it.end_year || "—"}</div>
                {it.field && <div className="text-sm mt-1">{it.field}</div>}
              </div>
              <Button variant="ghost" size="icon" onClick={() => del(it.id)} data-testid={`delete-education-${it.id}`}><Trash2 className="w-4 h-4" /></Button>
            </li>
          ))}
        </ul>
      )}
    </SectionShell>
  );
}

// ---------------- Skills ----------------
function SkillsSection({ items, reload }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", level: "Intermediate" });
  const submit = async () => {
    try {
      await api.post("/profile/skill", f);
      toast.success("Skill added");
      setOpen(false);
      setF({ name: "", level: "Intermediate" });
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/profile/skill/${id}`); reload(); };
  return (
    <SectionShell title="Skills" testidPrefix="skills" dialog={
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild><Button className="rounded-sm bg-ec-ink hover:bg-zinc-800 gap-2" data-testid="add-skill-trigger"><Plus className="w-4 h-4" /> Add</Button></DialogTrigger>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Add skill</DialogTitle></DialogHeader>
          <Field label="Name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
          <Field label="Level">
            <Select value={f.level} onValueChange={(v) => setF({ ...f, level: v })}>
              <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Beginner">Beginner</SelectItem>
                <SelectItem value="Intermediate">Intermediate</SelectItem>
                <SelectItem value="Advanced">Advanced</SelectItem>
                <SelectItem value="Expert">Expert</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <DialogFooter><Button onClick={submit} className="rounded-sm bg-ec-ink hover:bg-zinc-800" data-testid="save-skill-button">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    }>
      {items.length === 0 ? <EmptyState label="No skills added yet." /> : (
        <div className="p-6 flex flex-wrap gap-2">
          {items.map((s) => (
            <span key={s.id} className="inline-flex items-center gap-2 bg-ec-mute border border-ec-line px-3 py-1.5 text-sm rounded-sm">
              <span className="font-semibold">{s.name}</span>
              <span className="text-xs text-ec-ink-soft font-mono-plex">{s.level}</span>
              <button onClick={() => del(s.id)} className="text-ec-ink-soft hover:text-ec-error" data-testid={`delete-skill-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </span>
          ))}
        </div>
      )}
    </SectionShell>
  );
}

// ---------------- Achievements ----------------
function AchievementsSection({ items, reload }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", issuer: "", date: "", description: "" });
  const submit = async () => {
    try {
      await api.post("/profile/achievement", f);
      toast.success("Achievement added");
      setOpen(false);
      setF({ title: "", issuer: "", date: "", description: "" });
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/profile/achievement/${id}`); reload(); };
  return (
    <SectionShell title="Achievements" testidPrefix="achievements" dialog={
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild><Button className="rounded-sm bg-ec-ink hover:bg-zinc-800 gap-2" data-testid="add-achievement-trigger"><Plus className="w-4 h-4" /> Add</Button></DialogTrigger>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Add achievement</DialogTitle></DialogHeader>
          <Field label="Title"><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
          <Field label="Issuer"><Input value={f.issuer} onChange={(e) => setF({ ...f, issuer: e.target.value })} /></Field>
          <Field label="Date"><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
          <Field label="Description"><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
          <DialogFooter><Button onClick={submit} className="rounded-sm bg-ec-ink hover:bg-zinc-800" data-testid="save-achievement-button">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    }>
      {items.length === 0 ? <EmptyState label="No achievements yet." /> : (
        <ul className="divide-y divide-ec-line">
          {items.map((a) => (
            <li key={a.id} className="p-6 flex items-center justify-between">
              <div>
                <div className="font-display font-semibold text-lg">{a.title}</div>
                <div className="text-sm text-ec-ink-soft">{a.issuer} · {a.date}</div>
                {a.description && <p className="text-sm mt-1">{a.description}</p>}
              </div>
              <Button variant="ghost" size="icon" onClick={() => del(a.id)} data-testid={`delete-achievement-${a.id}`}><Trash2 className="w-4 h-4" /></Button>
            </li>
          ))}
        </ul>
      )}
    </SectionShell>
  );
}

// ---------------- Recommendations ----------------
const RELATIONSHIPS = ["Manager", "Founder", "Supervisor", "Client", "Professor", "Mentor", "Teacher", "Colleague", "Team Lead", "HR Manager"];
const REC_CATEGORIES = ["integrity", "professionalism", "communication", "leadership", "reliability", "teamwork", "problem_solving"];
const RATING_OPTIONS = ["Excellent", "Very Good", "Good", "Satisfactory", "Needs Improvement"];

function RecommendationsSection({ items, reload }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ recipient_ethic_id: "", relationship: "Colleague", message: "" });
  const [ratings, setRatings] = useState({});
  const submit = async () => {
    try {
      const payload = { ...f, ratings: Object.fromEntries(Object.entries(ratings).filter(([_, v]) => v)) };
      await api.post("/profile/recommendation", payload);
      toast.success("Recommendation written");
      setOpen(false);
      setF({ recipient_ethic_id: "", relationship: "Colleague", message: "" });
      setRatings({});
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <SectionShell title="Recommendations received" testidPrefix="recommendations" dialog={
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild><Button className="rounded-sm bg-ec-ink hover:bg-zinc-800 gap-2" data-testid="add-recommendation-trigger"><Plus className="w-4 h-4" /> Write one</Button></DialogTrigger>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Write a recommendation</DialogTitle></DialogHeader>
          <Field label="Recipient EthicCred ID"><Input placeholder="EC-XXXX-XXXX" value={f.recipient_ethic_id} onChange={(e) => setF({ ...f, recipient_ethic_id: e.target.value.toUpperCase() })} data-testid="rec-recipient-input" /></Field>
          <Field label="Relationship">
            <Select value={f.relationship} onValueChange={(v) => setF({ ...f, relationship: v })}>
              <SelectTrigger className="rounded-sm" data-testid="rec-relationship-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>
                {RELATIONSHIPS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Message"><Textarea rows={4} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="rec-message-input" /></Field>
          <div className="border border-ec-line bg-ec-soft p-3 rounded-sm">
            <div className="eyebrow mb-2">Ratings (optional)</div>
            <div className="space-y-2">
              {REC_CATEGORIES.map((cat) => (
                <div key={cat} className="flex items-center justify-between gap-3">
                  <span className="text-sm capitalize">{cat.replace("_", " ")}</span>
                  <Select value={ratings[cat] || ""} onValueChange={(v) => setRatings({ ...ratings, [cat]: v })}>
                    <SelectTrigger className="rounded-sm w-44 h-8" data-testid={`rec-rating-${cat}`}>
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      {RATING_OPTIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter><Button onClick={submit} className="rounded-sm bg-ec-ink hover:bg-zinc-800" data-testid="save-recommendation-button">Send</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    }>
      {items.length === 0 ? <EmptyState label="No recommendations yet." /> : (
        <ul className="divide-y divide-ec-line">
          {items.map((r) => (
            <li key={r.id} className="p-6">
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-ec-amber" />
                <span className="font-display font-semibold">{r.author_name}</span>
                <span className="text-sm text-ec-ink-soft">· {r.relationship}</span>
              </div>
              <p className="text-sm mt-2 italic">"{r.message}"</p>
              {r.ratings && Object.keys(r.ratings).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {Object.entries(r.ratings).map(([cat, val]) => (
                    <span key={cat} className="text-[10px] font-semibold bg-ec-mute border border-ec-line px-2 py-0.5 rounded-sm capitalize">
                      {cat.replace("_", " ")}: <span className="text-ec-emerald">{val}</span>
                    </span>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </SectionShell>
  );
}
